import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Trash2, Edit2, Eye, Wallet, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

const fmt = (v) => `R ${Number(v || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2 })}`;

const FundsPage = () => {
  const [funds, setFunds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showReport, setShowReport] = useState(null);
  const [report, setReport] = useState(null);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', external_id: '', fund_type: 'equity', total_capital: '', description: '', start_date: '', end_date: '' });

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try { const res = await api.get('/funds'); setFunds(res.data); }
    catch { setFunds([]); }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!form.name) return toast.error('Fund name is required');
    const payload = { ...form, total_capital: parseFloat(form.total_capital) || 0 };
    try {
      if (editId) { await api.put(`/funds/${editId}`, payload); toast.success('Fund updated'); }
      else { await api.post('/funds', payload); toast.success('Fund created'); }
      resetForm(); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleEdit = (f) => {
    setEditId(f.id);
    setForm({ name: f.name || '', external_id: f.external_id || '', fund_type: f.fund_type || 'equity', total_capital: f.total_capital?.toString() || '0', description: f.description || '', start_date: f.start_date || '', end_date: f.end_date || '' });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this fund?')) return;
    try { await api.delete(`/funds/${id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const viewReport = async (id) => {
    try { const res = await api.get(`/funds/${id}/report`); setReport(res.data); setShowReport(id); }
    catch { toast.error('Failed to load report'); }
  };

  const resetForm = () => {
    setShowModal(false); setEditId(null);
    setForm({ name: '', external_id: '', fund_type: 'equity', total_capital: '', description: '', start_date: '', end_date: '' });
  };

  const typeColors = { equity: 'bg-blue-50 text-blue-700', debt: 'bg-amber-50 text-amber-700', grant: 'bg-emerald-50 text-emerald-700', dfi: 'bg-purple-50 text-purple-700', mixed: 'bg-gray-50 text-gray-700' };

  return (
    <AdminSubPage title="Manage Funds" breadcrumb={['Admin', 'Organization', 'Manage Funds']} backPath="/dashboard/admin/organization" actionLabel="+ Create Fund" onAction={() => { resetForm(); setShowModal(true); }}>
      <p className="text-xs text-gray-500 mb-4">Define capital pools that finance your loan portfolio. Link funds to loan products to track deployment, returns, and investor contributions per fund.</p>
      <div className="bg-white border border-gray-200" data-testid="funds-table">
        <table className="w-full">
          <thead><tr className="bg-gray-50 border-b">
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Fund</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Type</th>
            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Total Capital</th>
            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Deployed</th>
            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Available</th>
            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Interest Earned</th>
            <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Loans</th>
            <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={8} className="text-center py-8 text-gray-400">Loading...</td></tr> :
            funds.length === 0 ? <tr><td colSpan={8} className="text-center py-8 text-gray-400">No funds defined. Create one to start tracking capital deployment.</td></tr> :
            funds.map(f => (
              <tr key={f.id} className="border-b hover:bg-gray-50" data-testid={`fund-row-${f.id}`}>
                <td className="px-4 py-3"><div className="font-medium text-sm text-[#1f1f1f]">{f.name}</div>{f.external_id && <div className="text-xs text-gray-400">{f.external_id}</div>}</td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-0.5 capitalize ${typeColors[f.fund_type] || typeColors.mixed}`}>{f.fund_type}</span></td>
                <td className="px-4 py-3 text-sm text-right font-mono">{fmt(f.total_capital)}</td>
                <td className="px-4 py-3 text-sm text-right font-mono">{fmt(f.deployed)}</td>
                <td className="px-4 py-3 text-sm text-right font-mono text-emerald-600">{fmt(f.available)}</td>
                <td className="px-4 py-3 text-sm text-right font-mono text-[#C5A059]">{fmt(f.interest_earned)}</td>
                <td className="px-4 py-3 text-sm text-right font-mono">{f.loan_count}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => viewReport(f.id)} className="text-blue-500"><Eye className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleEdit(f)} className="text-gray-500"><Edit2 className="w-4 h-4" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(f.id)} className="text-red-400"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Fund Report Modal */}
      {showReport && report && (
        <Dialog open={!!showReport} onOpenChange={() => setShowReport(null)}>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" aria-describedby="report-desc">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><TrendingUp className="w-5 h-5 text-[#C5A059]" />Fund Report: {report.fund_name}</DialogTitle></DialogHeader>
            <p id="report-desc" className="sr-only">Fund P&L report</p>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-gray-50 border"><p className="text-[10px] text-gray-500 uppercase">Total Capital</p><p className="text-lg font-bold">{fmt(report.total_capital)}</p></div>
                <div className="p-3 bg-gray-50 border"><p className="text-[10px] text-gray-500 uppercase">Deployed</p><p className="text-lg font-bold">{fmt(report.total_outstanding)}</p></div>
                <div className="p-3 bg-gray-50 border"><p className="text-[10px] text-gray-500 uppercase">Available</p><p className="text-lg font-bold text-emerald-600">{fmt(report.available)}</p></div>
                <div className="p-3 bg-gray-50 border"><p className="text-[10px] text-gray-500 uppercase">ROI</p><p className="text-lg font-bold text-[#C5A059]">{report.roi_percent}%</p></div>
              </div>
              <div className="border-t pt-3">
                <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Revenue</p>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-gray-600">Interest Earned</span><span className="font-mono">{fmt(report.total_interest_earned)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Fees Earned</span><span className="font-mono">{fmt(report.total_fees_earned)}</span></div>
                  <div className="flex justify-between font-semibold border-t pt-1"><span>Total Revenue</span><span className="font-mono">{fmt(report.total_revenue)}</span></div>
                </div>
              </div>
              {report.investors?.length > 0 && (
                <div className="border-t pt-3">
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Investors</p>
                  {report.investors.map((inv, i) => (
                    <div key={i} className="flex justify-between text-sm py-1">
                      <span className="text-gray-600">{inv.investor_name}</span>
                      <span className="font-mono">{fmt(inv.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Create/Edit Fund Modal */}
      <Dialog open={showModal} onOpenChange={v => { if (!v) resetForm(); }}>
        <DialogContent aria-describedby="fund-desc"><DialogHeader><DialogTitle>{editId ? 'Edit Fund' : 'Create Fund'}</DialogTitle></DialogHeader>
          <p id="fund-desc" className="sr-only">Form to manage a fund</p>
          <div className="space-y-4">
            <div><Label>Fund Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="fund-name" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Fund Type</Label>
                <Select value={form.fund_type} onValueChange={v => setForm({...form, fund_type: v})}>
                  <SelectTrigger data-testid="fund-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="equity">Equity</SelectItem>
                    <SelectItem value="debt">Debt / Credit Line</SelectItem>
                    <SelectItem value="grant">Grant / Donor</SelectItem>
                    <SelectItem value="dfi">DFI Facility</SelectItem>
                    <SelectItem value="mixed">Mixed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Total Capital (R)</Label><Input type="number" value={form.total_capital} onChange={e => setForm({...form, total_capital: e.target.value})} data-testid="fund-capital" /></div>
            </div>
            <div><Label>External ID</Label><Input value={form.external_id} onChange={e => setForm({...form, external_id: e.target.value})} placeholder="e.g., DFI-2025-001" /></div>
            <div><Label>Description</Label><Input value={form.description} onChange={e => setForm({...form, description: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} /></div>
              <div><Label>End Date</Label><Input type="date" value={form.end_date} onChange={e => setForm({...form, end_date: e.target.value})} /></div>
            </div>
          </div>
          <DialogFooter><Button variant="outline" onClick={resetForm}>Cancel</Button><Button onClick={handleSubmit} className="bg-[#1f1f1f] text-white" data-testid="fund-submit">{editId ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminSubPage>
  );
};
export default FundsPage;
